// @dx utility
import React from "react";
import { App, Button, Progress } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import type { NotificationProvider } from "@refinedev/core";

/**
 * Custom notification provider that uses antd `message` (small centered toasts)
 * instead of the default `notification` (big boxes in the top-right corner).
 *
 * Success notifications are suppressed — every form/modal that cares already
 * shows its own message.success(). Showing Refine's generic ones on top would
 * duplicate them (and they arrive untranslated from the English fallback).
 *
 * Errors are always shown — they may not have component-level handling.
 *
 * The undoable/progress type still uses `notification` because it needs a
 * persistent dismissible widget with a countdown — `message` can't do that.
 */
export function useCustomNotificationProvider(): NotificationProvider {
  const { message, notification } = App.useApp();

  return {
    open: ({ key, message: msg, description, type, cancelMutation, undoableTimeout }) => {
      if (type === "progress") {
        // Undoable mutation countdown — must be persistent and dismissible
        notification.open({
          key,
          message: null,
          description: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: -7 }}>
              <Progress
                type="circle"
                percent={(undoableTimeout ?? 0) * 20}
                format={(t) => t && t / 20}
                size={50}
                strokeColor="#1890ff"
                status="normal"
              />
              <span style={{ marginLeft: 8, width: "100%" }}>{msg}</span>
              <Button
                style={{ flexShrink: 0 }}
                onClick={() => {
                  cancelMutation?.();
                  notification.destroy(key ?? "");
                }}
                disabled={undoableTimeout === 0}
                icon={<UndoOutlined />}
              />
            </div>
          ),
          duration: 0,
          closeIcon: <></>,
        });
      } else if (type === "success") {
        // Suppressed — components show their own success messages
      } else if (type === "error") {
        // description = actual error text, msg = translated label ("Błąd podczas zapisu...")
        message.error(description ?? msg, 5);
      } else if (type === "info") {
        message.info(description ?? msg, 3);
      } else {
        message.warning(description ?? msg, 4);
      }
    },
    close: (key) => notification.destroy(key),
  };
}